import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/deploy/components/ui/Icon';
import { Button } from '@/deploy/components/ui/Button';
import { useSessionStore } from '@/deploy/stores/session.store';
import { isDeployApiConfigured, API_BASE } from '@/deploy/services/api';
import styles from './Login.module.scss';

export function Login() {
  const navigate = useNavigate();
  const login = useSessionStore((s) => s.login);
  const isLoggedIn = useSessionStore((s) => s.isLoggedIn);
  const apiConfigured = isDeployApiConfigured();

  // Si déjà loggé (revient sur /login manuellement) OU dès que le login réussit,
  // on renvoie vers le dashboard de l'espace Déploiement.
  useEffect(() => {
    if (isLoggedIn) navigate('/deploiement', { replace: true });
  }, [isLoggedIn, navigate]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (!result.ok) setError(result.reason);
  };

  return (
    <div className={styles.page}>
      <div className={styles.bg} aria-hidden />

      {!apiConfigured && (
        <div style={{
          position: 'absolute', top: 12, left: 12, right: 12, zIndex: 10,
          padding: '10px 14px',
          background: 'var(--color-warning-bg)', color: 'var(--color-warning)',
          border: '1px solid currentColor', borderRadius: 8, fontSize: 13,
        }}>
          <strong>Configuration manquante</strong> — l'URL de l'API recube-cicd
          n'est pas définie. Ajoute <code>VITE_DEPLOY_API_URL=https://ton-api</code>
          dans <code>packages/web/.env</code> puis relance <code>pnpm dev</code>.
        </div>
      )}
      {apiConfigured && API_BASE && (
        <div style={{
          position: 'absolute', top: 12, right: 12, zIndex: 10,
          padding: '4px 10px',
          background: 'var(--color-surface)', color: 'var(--color-text-muted)',
          border: '1px solid var(--color-border)', borderRadius: 999, fontSize: 11,
        }}>
          API : <code>{API_BASE}</code>
        </div>
      )}

      <div className={styles.split}>
        {/* -------- Colonne gauche : branding -------- */}
        <aside className={styles.brandPanel}>
          <div className={styles.brandLogo}>
            <Icon name="layers" size={28} />
          </div>
          <div className={styles.brandTitle}>
            <h1>
              Recube <span>CI/CD</span>
            </h1>
            <p>Panel interne de déploiement · Paladium Bedrock</p>
          </div>
          <ul className={styles.brandPoints}>
            <li><Icon name="rocket" size={14} /> Déploiement en 1 clic · Pré-prod → Prod</li>
            <li><Icon name="layers" size={14} /> Envs de dev perso · slots Bêta A→E</li>
            <li><Icon name="package" size={14} /> Containers Minecraft & APIs · CI/CD unifiée</li>
            <li><Icon name="eye" size={14} /> Console, logs, file explorer par service</li>
          </ul>
          <footer className={styles.brandFooter}>
            <span>API · MongoDB</span>
            <span>Recube v1.0.0</span>
          </footer>
        </aside>

        {/* -------- Colonne droite : form -------- */}
        <main className={styles.formPanel}>
          <div className={styles.formCard}>
            <header className={styles.formHeader}>
              <h2>Connexion</h2>
              <p>Utilise un compte existant ou pick un profil de test ci-dessous.</p>
            </header>

            <form onSubmit={onSubmit} className={styles.form}>
              <label className={styles.field}>
                <span>Email</span>
                <div className={styles.inputWrap}>
                  <Icon name="user" size={14} />
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="prenom.nom@paladium.games"
                  />
                </div>
              </label>

              <label className={styles.field}>
                <span>Mot de passe</span>
                <div className={styles.inputWrap}>
                  <Icon name="lock" size={14} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className={styles.inputBtn}
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Masquer' : 'Afficher'}
                  >
                    <Icon name="eye" size={14} />
                  </button>
                </div>
              </label>

              {error && (
                <div className={styles.error}>
                  <Icon name="warn" size={14} />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                rightIcon={!loading ? <Icon name="arrow-right" size={16} /> : undefined}
              >
                Se connecter
              </Button>

              <div className={styles.formLinks}>
                <button type="button" className={styles.link}>Mot de passe oublié ?</button>
                <span className={styles.sep} />
                <button type="button" className={styles.link}>SSO Paladium</button>
              </div>
            </form>

          </div>

          <footer className={styles.formFooter}>
            Recube CI/CD &copy; {new Date().getFullYear()} Paladium Games
          </footer>
        </main>
      </div>
    </div>
  );
}
